import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InvoiceChargesService } from 'src/app/services/invoice-charges/invoice-charges.service';
import { InvoiceChargesModalService } from 'src/app/services/invoice-charges-modal/invoice-charges-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-invoice-charge',
  templateUrl: './add-edit-invoice-charge.component.html',
  styleUrls: ['./add-edit-invoice-charge.component.scss']
})
export class AddEditInvoiceChargeComponent extends DestructorComponent implements OnInit {

  @Output() invoiceChargeAdded = new EventEmitter<void>();
  @Output() invoiceChargeUpdated = new EventEmitter<void>();

  invoiceChargeForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedInvoiceCharge: any = null;

  constructor(
    private fb: FormBuilder,
    private _invoiceChargesService: InvoiceChargesService,
    private _invoiceChargesModalService: InvoiceChargesModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.invoiceChargeForm = this.fb.group({
      label: ['', [Validators.required, Validators.minLength(2)]],
      usd: [0, [Validators.required, Validators.min(0)]],
      jpy: [0, [Validators.required, Validators.min(0)]]
    });
  }

  // Getter methods for form controls
  get label() { return this.invoiceChargeForm.get('label'); }
  get usd() { return this.invoiceChargeForm.get('usd'); }
  get jpy() { return this.invoiceChargeForm.get('jpy'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_invoice_charge_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceInvoiceCharge = this._invoiceChargesModalService.getSelectedInvoiceCharge();
        if (serviceInvoiceCharge) {
          this.isEditMode = true;
          this.selectedInvoiceCharge = serviceInvoiceCharge;
          this.populateForm(this.selectedInvoiceCharge);
        } else {
          this.isEditMode = false;
          this.selectedInvoiceCharge = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._invoiceChargesModalService.clearSelectedInvoiceCharge();
        this.resetForm();
      });
    }
  }

  private populateForm(invoiceCharge: any): void {
    this.invoiceChargeForm.patchValue({
      label: invoiceCharge.label || '',
      usd: invoiceCharge.usd || 0,
      jpy: invoiceCharge.jpy || 0
    });
  }

  private resetForm(): void {
    this.invoiceChargeForm.reset();
    this.invoiceChargeForm.patchValue({
      label: '',
      usd: 0,
      jpy: 0
    });
    this.isLoading = false;
    this.markFormGroupUntouched(this.invoiceChargeForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_invoice_charge_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.invoiceChargeForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.invoiceChargeForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._invoiceChargesService.UpdateData(this.selectedInvoiceCharge._id, submitData)
        : this._invoiceChargesService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Invoice charge operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Invoice charge has been successfully updated!"
                : "Invoice charge has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.invoiceChargeUpdated.emit();
                } else {
                  this.invoiceChargeAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Invoice charge operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.invoiceChargeForm);
    }
  }
}
